import {
  type Child,
  el,
  type ElNode,
  type Instruction,
  isString,
  type MaybeDynamic,
  raw,
  reader,
  type Reader,
  safeHtml,
} from '@crown/framework/ir';
import type { BrandColor } from '../types';
import template from './empty-state.tsrx';

import './empty-state.css';

export type EmptyStateVariant = 'default' | 'compact' | 'minimal';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: BrandColor | 'primary' | 'secondary' | 'ghost';
}

export interface EmptyStateProps {
  title: MaybeDynamic<string>;
  description?: MaybeDynamic<string | undefined>;
  icon?: string | HTMLElement;
  illustration?: string | HTMLElement;
  actions?: EmptyStateAction[];
  variant?: EmptyStateVariant;
  iconVariant?: BrandColor;
  extraClasses?: string | string[];
  /**
   * The heading level the title takes, 1 to 6.
   *
   * An empty state usually stands for a section of the page, so it sits one
   * below the page's own heading. It was fixed at 3, which skipped a level on
   * every page whose only other heading is the h1 - a reader listing headings
   * finds a gap where the missing level was. Pass the level this one belongs
   * under where the surrounding structure says otherwise.
   */
  headingLevel?: number;
}

const EmptyStateTemplate: (props: EmptyStateTemplateProps) => Instruction = template;

/** The icon block: its classes, the inline size a plain glyph needs, and its content. */
export interface EmptyStateIcon {
  readonly classes: string;
  readonly style: string | null;
  readonly content: Child;
}

export interface EmptyStateTemplateProps {
  readonly classes: string;
  readonly illustration: Child | undefined;
  readonly icon: EmptyStateIcon | undefined;
  readonly heading: Instruction;
  readonly description: Reader<string | undefined>;
  readonly actions: readonly Instruction[];
}

function containerClasses({ variant = 'default', extraClasses }: EmptyStateProps): string {
  const classes = ['empty-state'];
  if (variant !== 'default') classes.push(variant);
  if (extraClasses) classes.push(...(Array.isArray(extraClasses) ? extraClasses : [extraClasses]));
  return classes.join(' ');
}

/** An element is adopted, inline markup is its own, and anything else is an image source. */
function illustrationChild(illustration: EmptyStateProps['illustration']): Child | undefined {
  if (illustration === undefined) return undefined;
  if (!isString(illustration)) return illustration;
  if (illustration.startsWith('<svg') || illustration.startsWith('<img')) {
    return raw(safeHtml(illustration));
  }
  return el('img', { src: illustration, alt: '', 'aria-hidden': 'true' });
}

/** An element or inline SVG is the icon itself; anything else is a glyph, which needs a size. */
function iconBlock(props: EmptyStateProps): EmptyStateIcon | undefined {
  const { icon, iconVariant, illustration } = props;
  if (icon === undefined || illustration !== undefined) return undefined;
  const classes = iconVariant ? `empty-state-icon ${iconVariant}` : 'empty-state-icon';
  if (!isString(icon)) return { classes, style: null, content: icon };
  if (icon.startsWith('<svg')) return { classes, style: null, content: raw(safeHtml(icon)) };
  return { classes: `${classes} text-3xl`, style: null, content: icon };
}

/** The heading level, within 1 to 6; anything else is the default. */
function headingTag(headingLevel: number): string {
  const level = Number.isInteger(headingLevel) && headingLevel >= 1 && headingLevel <= 6 ? headingLevel : 2;
  return `h${level}`;
}

function actionButtons(actions: readonly EmptyStateAction[]): Instruction[] {
  return actions.map((action) =>
    el('button', { type: 'button', class: `btn ${action.variant ?? 'primary'}`, onClick: action.onClick }, action.label),
  );
}

export function EmptyState(props: EmptyStateProps): ElNode {
  const node = EmptyStateTemplate({
    classes: containerClasses(props),
    illustration: illustrationChild(props.illustration),
    icon: iconBlock(props),
    heading: el(headingTag(props.headingLevel ?? 2), { class: 'empty-state-title' }, reader(props.title)),
    description: reader(props.description),
    actions: actionButtons(props.actions ?? []),
  });
  if (node.t !== 'el') throw new Error('EmptyState renders one element');
  return node;
}

export const EmptyStateIcons = {
  folder: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>`,
  document: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>`,
  search: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>`,
  inbox: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"></polyline><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path></svg>`,
  list: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>`,
  users: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>`,
  announcement: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>`,
  menu: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="6" x2="20" y2="6"></line><line x1="4" y1="12" x2="20" y2="12"></line><line x1="4" y1="18" x2="20" y2="18"></line></svg>`,
  plus: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>`,
  shield: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>`,
  layers: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>`,
  layout: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>`,
  branch: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="6" y1="3" x2="6" y2="15"></line><circle cx="18" cy="6" r="3"></circle><circle cx="6" cy="18" r="3"></circle><path d="M18 9a9 9 0 0 1-9 9"></path></svg>`,
  clipboard: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><line x1="9" y1="12" x2="15" y2="12"></line><line x1="9" y1="16" x2="15" y2="16"></line></svg>`,
  puzzle: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19.4 12.6a1.5 1.5 0 1 0 0-3H18V6a2 2 0 0 0-2-2h-3.6a1.5 1.5 0 1 0-3 0H6a2 2 0 0 0-2 2v3.4a1.5 1.5 0 1 1 0 3V18a2 2 0 0 0 2 2h3.4a1.5 1.5 0 1 1 3 0H16a2 2 0 0 0 2-2v-3.4h1.4z"></path></svg>`,
  location: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>`,
};
